import React, { Component } from 'react';
import PropTypes from 'prop-types';

/**
 * Enhanced ErrorBoundary component to catch errors in React components
 * and display a fallback UI instead of crashing the entire app.
 * 
 * Features:
 * - Custom fallback UI via props
 * - Error logging
 * - Easy refresh button
 * - Component recovery attempt
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    // Store error info in state
    this.setState({ errorInfo });
    
    // Send to error reporting service if configured
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (reportingError) {
        console.error('Error in error reporting:', reportingError);
      }
    }
  }
  
  // Reset the error state to attempt recovery
  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'An unexpected error occurred';
      
      // Use custom fallback if provided
      if (this.props.fallback) {
        if (React.isValidElement(this.props.fallback)) {
          return this.props.fallback;
        } else if (typeof this.props.fallback === 'function') {
          return this.props.fallback({
            error: this.state.error,
            errorInfo: this.state.errorInfo,
            resetError: this.resetErrorBoundary
          });
        }
      }
      
      // Default fallback UI
      return (
        <div className="error-boundary p-4 flex flex-col items-center justify-center min-h-[50vh] bg-gray-100">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
            <h1 className="text-xl font-semibold text-red-600 mb-4">Something went wrong</h1>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <p className="text-red-700">{errorMessage}</p>
            </div>
            
            <h2 className="text-lg font-medium mb-2">What happened?</h2>
            <p className="mb-4 text-gray-600">
              The application encountered an error that prevented it from rendering correctly. 
              This could be due to a temporary issue or a bug in the code.
            </p>
            
            <h2 className="text-lg font-medium mb-2">What can you do?</h2>
            <ul className="list-disc pl-5 mb-4 text-gray-600">
              <li>Try refreshing the page</li>
              <li>Clear your browser cache</li>
              <li>Try again later</li>
            </ul>
            
            <div className="flex space-x-3">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </button>
              
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                onClick={this.resetErrorBoundary}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children; 
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func
  ]),
  onError: PropTypes.func
};

export default ErrorBoundary;